import { CoreTypes, type ObjectRecord, type TextStyle } from '@vp/shared-types';
import { canvasMeasure, fontString } from './text';

/**
 * Flowchart forms of a rectangle (`props.form`): drawn as paths, otherwise a rectangle in every way
 * (fill, stroke, text, connectors). `props.flip` mirrors left ↔ right.
 *  - `manual` — manual input: the top edge slopes up to the right;
 *  - `data` — parallelogram;
 *  - `bracket` — "[" at the left, the text beside it;
 *  - `brace` — "}" at the left pointing at the text beside it;
 *  - `document` — rounded top, wavy bottom;
 *  - `display` — pointed at the left, bulging at the right.
 */
export type RectForm = 'manual' | 'data' | 'bracket' | 'brace' | 'document' | 'display';
export const RECT_FORMS: readonly RectForm[] = ['manual', 'data', 'bracket', 'brace', 'document', 'display'];

export interface Inset {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export function rectForm(o: Pick<ObjectRecord, 'type' | 'props'>): RectForm | null {
  if (o.type !== CoreTypes.rect) return null;
  const f = o.props['form'];
  return typeof f === 'string' && (RECT_FORMS as readonly string[]).includes(f) ? (f as RectForm) : null;
}

/** Width of the bracket / brace band at the side. */
function band(form: RectForm, w: number, h: number): number {
  return Math.min(Math.max(form === 'brace' ? 16 : 10, h * (form === 'brace' ? 0.14 : 0.1)), w * 0.3);
}

const slope = (h: number) => h * 0.3;
const wave = (h: number) => h * 0.1;
const skew = (w: number, h: number) => Math.min(h * 0.35, w * 0.25);

/** Outline of a form in local coords (flat x,y list) and whether it is closed. */
export function formOutline(form: RectForm, w: number, h: number, radius: number, flip: boolean): { points: number[]; closed: boolean } {
  let points: number[];
  let closed = true;
  if (form === 'manual') points = roundedPolygon([0, slope(h), w, 0, w, h, 0, h], radius);
  else if (form === 'data') {
    const s = skew(w, h);
    points = roundedPolygon([s, 0, w, 0, w - s, h, 0, h], radius);
  } else if (form === 'document') {
    const r = Math.min(radius, w / 4, h / 4);
    const a = wave(h);
    points = [0, r];
    quad(points, 0, r, 0, 0, r, 0);
    points.push(w - r, 0);
    quad(points, w - r, 0, w, 0, w, r);
    // the bottom: one wave, high at the left, a trough, a crest, down to the right corner
    for (let i = 0; i <= 32; i++) {
      const t = 1 - i / 32;
      points.push(t * w, h - a + a * Math.sin(2 * Math.PI * t * 1.15 - 0.6));
    }
  } else if (form === 'display') {
    const s = Math.min(h * 0.3, w * 0.25);
    const c = Math.min(h * 0.12, w * 0.1);
    const poly = [s, 0, w - c, 0];
    // the right side bulges out to the box edge
    quad(poly, w - c, 0, w + c, h / 2, w - c, h, 12);
    poly.push(s, h, 0, h / 2);
    points = roundedPolygon(poly, Math.min(radius, h / 6));
  } else if (form === 'bracket') {
    const b = band(form, w, h);
    points = [b, 0, 0, 0, 0, h, b, h];
    closed = false;
  } else {
    const b = band(form, w, h);
    const m = b / 2;
    const r = Math.min(m, h / 4);
    points = [0, 0];
    quad(points, 0, 0, m, 0, m, r);
    points.push(m, h / 2 - r);
    quad(points, m, h / 2 - r, m, h / 2, b, h / 2);
    quad(points, b, h / 2, m, h / 2, m, h / 2 + r);
    points.push(m, h - r);
    quad(points, m, h - r, m, h, 0, h);
    closed = false;
  }
  if (flip) for (let i = 0; i < points.length; i += 2) points[i] = w - points[i];
  return { points, closed };
}

/** Text area of a form: inset from the object's box (the editor uses the same). */
export function formTextInset(o: Pick<ObjectRecord, 'type' | 'props' | 'w' | 'h'>): Inset | null {
  const form = rectForm(o);
  if (!form) return null;
  const flip = !!o.props['flip'];
  const side = (n: number): Inset => (flip ? { top: 0, right: n, bottom: 0, left: 0 } : { top: 0, right: 0, bottom: 0, left: n });
  if (form === 'manual') return { top: slope(o.h) * 0.6, right: 0, bottom: 0, left: 0 };
  if (form === 'data') {
    const s = skew(o.w, o.h) * 0.7;
    return { top: 0, right: s, bottom: 0, left: s };
  }
  if (form === 'document') return { top: 0, right: 0, bottom: wave(o.h) * 2, left: 0 };
  if (form === 'display') return { top: 0, right: Math.min(o.h * 0.12, o.w * 0.1), bottom: 0, left: Math.min(o.h * 0.3, o.w * 0.25) * 0.6 };
  return side(band(form, o.w, o.h) + 4);
}

/**
 * Text style of a form: the size follows the shape — as large as fits its text area (by width and
 * about half its height), so the text grows and shrinks with the shape. `text` overrides the object's.
 */
export function formTextStyle(o: Pick<ObjectRecord, 'type' | 'props' | 'w' | 'h'>, base: TextStyle, text?: string): TextStyle {
  const ins = formTextInset(o);
  if (!ins) return base;
  const tw = Math.max(1, o.w - ins.left - ins.right);
  const th = Math.max(1, o.h - ins.top - ins.bottom);
  const raw = text ?? (typeof o.props['text'] === 'string' ? (o.props['text'] as string) : '');
  const lines = (raw || 'Text').split('\n');
  const lh = base.lineHeight ?? 1.25;
  const font = fontString(base, 100);
  const em = Math.max(...lines.map((l) => canvasMeasure(l || ' ', font) / 100), 0.5);
  // padding is 0.45 em on each side (textPadding)
  const byWidth = tw / (em + 0.9 + 0.2);
  const byHeight = (th * 0.5) / (lines.length * lh);
  const room = (th - 2) / (lines.length * lh + 0.9);
  return { ...base, fontSize: Math.max(4, Math.min(byWidth, byHeight, room)) };
}

/** Quadratic bezier from (x0,y0) via (cx,cy) to (x1,y1), appended without its first point. */
function quad(out: number[], x0: number, y0: number, cx: number, cy: number, x1: number, y1: number, steps = 8): void {
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    out.push(u * u * x0 + 2 * u * t * cx + t * t * x1, u * u * y0 + 2 * u * t * cy + t * t * y1);
  }
}

/** A closed polygon with its corners rounded by `r` (kept within half of each edge). */
export function roundedPolygon(poly: number[], r: number): number[] {
  const n = poly.length / 2;
  if (r <= 0) return poly.slice();
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const px = poly[((i + n - 1) % n) * 2];
    const py = poly[((i + n - 1) % n) * 2 + 1];
    const x = poly[i * 2];
    const y = poly[i * 2 + 1];
    const nx = poly[((i + 1) % n) * 2];
    const ny = poly[((i + 1) % n) * 2 + 1];
    const l1 = Math.hypot(x - px, y - py);
    const l2 = Math.hypot(nx - x, ny - y);
    const k = Math.min(r, l1 / 2, l2 / 2);
    if (k <= 0 || l1 === 0 || l2 === 0) {
      out.push(x, y);
      continue;
    }
    const ax = x + ((px - x) / l1) * k;
    const ay = y + ((py - y) / l1) * k;
    const bx = x + ((nx - x) / l2) * k;
    const by = y + ((ny - y) / l2) * k;
    out.push(ax, ay);
    quad(out, ax, ay, x, y, bx, by, 6);
  }
  return out;
}
